import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

// Strip '/api' suffix from the REST base URL — socket.io mounts at the server root.
const resolveSocketUrl = () => {
  const raw = process.env.REACT_APP_API_BASE_URL || '';
  return raw.replace(/\/api\/?$/, '') || undefined;
};

// One till at a time per (store, product) runs a coupon round. This hook opens
// an "operator" socket for the Coupon Sales page and holds the SAME slot the
// mobile tills use, so a web round and a till round for the same store + product
// serialise against each other.
//
// STRICT wait: acquire() blocks until the current holder finishes the sale or
// leaves the screen — it does not auto-proceed on a timeout. The wait is bounded
// only by the server's TTL backstop, which force-frees a wedged holder. Every
// path where the socket itself is unavailable (no socket, not connected) still
// fails OPEN and lets the round proceed — the DB's unique indexes are the real
// guard; blocking a sale because a socket is unhappy would be self-inflicted.
export default function useSaleLock(userName) {
  const socketRef = useRef(null);
  const freeListeners = useRef(new Set());
  const activeToken = useRef(null);
  // Store currently being observed, re-sent after a reconnect so the busy hint
  // doesn't go stale when the socket drops and comes back. Read in the 'connect'
  // handler and in watch().
  const watchedStoreRef = useRef(null);
  const [waiting, setWaiting] = useState(false);
  // productId -> holder label, for the currently watched store.
  const [busyMap, setBusyMap] = useState(() => new Map());

  useEffect(() => {
    const socket = io(resolveSocketUrl(), {
      auth: {
        apiKey: process.env.REACT_APP_API_KEY || '',
        role: 'operator',
        userName: userName || 'Web',
      },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    const setBusy = (productId, holder) => {
      if (!productId) return;
      setBusyMap(prev => {
        const next = new Map(prev);
        next.set(productId, holder || 'another user');
        return next;
      });
    };
    const clearBusy = (productId) => {
      if (!productId) return;
      setBusyMap(prev => {
        if (!prev.has(productId)) return prev;
        const next = new Map(prev);
        next.delete(productId);
        return next;
      });
    };

    socket.on('sale:busy', ({ productId, holder }) => setBusy(productId, holder));
    socket.on('sale:free', (payload) => {
      clearBusy(payload?.productId);
      freeListeners.current.forEach(fn => {
        try { fn(payload); } catch (e) { /* listener errors are non-fatal */ }
      });
    });
    socket.on('sale:state', ({ busyProducts = [] }) => {
      setBusyMap(new Map(busyProducts.map(pid => [pid, 'another user'])));
    });
    // Re-observe the selected store after a (re)connect so the busy hint stays live.
    socket.on('connect', () => {
      if (watchedStoreRef.current) {
        socket.emit('sale:watch', { storeId: watchedStoreRef.current }, (res) => {
          const bp = res?.busyProducts || [];
          setBusyMap(new Map(bp.map(pid => [pid, 'another user'])));
        });
      }
    });
    socket.on('connect_error', (err) => {
      console.warn('[SaleLock] connect_error', err?.message || err);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userName]);

  // Observe a store's busy slots (call on store select).
  const watch = useCallback((storeId) => {
    const socket = socketRef.current;
    if (!socket || !storeId) return;
    watchedStoreRef.current = storeId;
    // When disconnected, the persistent 'connect' handler re-emits the watch, so
    // we only need to fire now if we're already connected.
    if (socket.connected) {
      socket.emit('sale:watch', { storeId }, (res) => {
        const bp = res?.busyProducts || [];
        setBusyMap(new Map(bp.map(pid => [pid, 'another user'])));
      });
    }
  }, []);

  const askOnce = useCallback((storeId, productId) => new Promise((resolve) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) {
      resolve({ granted: true, reason: 'no-socket' });
      return;
    }
    let settled = false;
    const t = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({ granted: true, reason: 'ack-timeout' });
    }, 4000);
    try {
      socket.emit('sale:acquire', { storeId, productId }, (res) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        resolve(res || { granted: true, reason: 'no-ack' });
      });
    } catch (e) {
      if (settled) return;
      settled = true;
      clearTimeout(t);
      resolve({ granted: true, reason: 'emit-failed' });
    }
  }), []);

  // Blocks until the slot is ours, or until cancel() is called. Resolves with
  // { granted:false, reason:'cancelled' } if the round was abandoned mid-wait.
  const acquire = useCallback(async (storeId, productId) => {
    if (!storeId || !productId) return { granted: true, reason: 'no-args' };
    const token = { cancelled: false, wake: null };
    activeToken.current = token;
    setWaiting(true);
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (token.cancelled) return { granted: false, reason: 'cancelled' };
        const res = await askOnce(storeId, productId);
        if (res.granted) return res;
        // Blocked by another till/operator. Wait for it to finish or leave
        // (sale:free), a periodic re-check, or cancellation — then try again.
        await new Promise((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            freeListeners.current.delete(onFree);
            token.wake = null;
            resolve();
          };
          const onFree = (payload) => {
            if (payload?.productId && payload.productId !== productId) return;
            finish();
          };
          freeListeners.current.add(onFree);
          const timer = setTimeout(finish, 2000);
          token.wake = finish;
        });
      }
    } finally {
      setWaiting(false);
      if (activeToken.current === token) activeToken.current = null;
    }
  }, [askOnce]);

  // Abort an in-flight acquire (store/product change, new round, unmount).
  const cancel = useCallback(() => {
    const token = activeToken.current;
    if (!token) return;
    token.cancelled = true;
    if (token.wake) token.wake();
  }, []);

  const release = useCallback((storeId, productId) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected || !storeId || !productId) return;
    try {
      socket.emit('sale:release', { storeId, productId });
    } catch (e) {
      console.warn('[SaleLock] release failed', e?.message);
    }
  }, []);

  return { acquire, release, watch, cancel, waiting, busyMap };
}
